import Phaser from "phaser";
import { WalkChapter } from "./WalkChapter";
import { OBRAS } from "../data/obras";
import type { ChapterData } from "../data/content-types";
import type { InteractableDef } from "../core/types";
import { TILE_W, TILE_H, isoDepth } from "../core/iso";
import * as P from "../art/props";

/**
 * CAPÍTULO 1 — "El casco amarillo".
 * VERTICAL SLICE VISUAL: sobre la MISMA lógica de WalkChapter, se sustituye
 * la representación (props isométricos, suelo variado, decorado, cámara).
 */
export class ObrasScene extends WalkChapter {
  protected chapter: ChapterData = OBRAS;
  protected grid = { w: 14, h: 10 };
  protected spawn = { x: 7, y: 8 };
  protected hideLabels = true; // la info se descubre con [E], no con etiquetas fijas
  protected worldSigns = [
    { id: "m_casco", x: 0.7, y: 5 },
    { id: "w_caida", x: 2.7, y: 0.5 },
    { id: "w_objetos", x: 13.2, y: 2.2 },
  ];

  private decor: Phaser.GameObjects.GameObject[] = [];
  private platform?: Phaser.GameObjects.Container;
  /** Borde elevado (mundo): a partir de aquí hacia el fondo es el forjado, no transitable. */
  private readonly edgeY = 2.6;
  /** Geometría del forjado elevado (compartida por buildPlatform y onResolved). */
  private readonly plat = { hw: TILE_W * 2.3, hd: TILE_H * 1.5, th: 16, lift: 48 };

  /** El forjado elevado no es transitable: EPI investiga el borde desde abajo. */
  protected isBlocked(_x: number, y: number): boolean {
    return y < this.edgeY;
  }

  constructor() {
    super("obras");
  }

  // ---------- fondo: calima de obra (nunca vacío negro) ----------
  protected buildBackdrop(): void {
    const cam = this.cameras.main;
    cam.setBackgroundColor(0x9a8560); // polvo/tierra: rellena todo lo que no es suelo
    // cielo con calima que se funde con el polvo, fijo a la cámara
    const W = 1280, H = 720, over = 420;
    const sky = this.add.graphics().setScrollFactor(0).setDepth(-19000);
    sky.fillGradientStyle(0xcfc19d, 0xcfc19d, 0x9a8560, 0x9a8560, 1);
    sky.fillRect(-over, -over, W + over * 2, H * 0.62 + over);
    // franja de calima difusa en el horizonte
    const haze = this.add.graphics().setScrollFactor(0).setDepth(-18980);
    haze.fillStyle(0xbfae86, 0.5);
    haze.fillRect(-over, H * 0.34, W + over * 2, 70);
  }

  // ---------- cámara: acercarse para apreciar el detalle ----------
  protected setupCamera(): void {
    const cam = this.cameras.main;
    // límites del recinto: la cámara no vaga hacia el vacío
    const pts = [this.iso(0, 0), this.iso(this.grid.w - 1, 0), this.iso(0, this.grid.h - 1), this.iso(this.grid.w - 1, this.grid.h - 1)];
    const xs = pts.map((p) => p.sx), ys = pts.map((p) => p.sy);
    const minX = Math.min(...xs) - TILE_W, maxX = Math.max(...xs) + TILE_W;
    const minY = Math.min(...ys) - 280, maxY = Math.max(...ys) + TILE_H * 2.5;
    cam.setBounds(minX, minY, maxX - minX, maxY - minY);
    cam.setZoom(1.3);
    cam.startFollow(this.player.container, true, 0.12, 0.12);
    cam.setDeadzone(160, 110);
  }

  // ---------- suelo variado por zonas ----------
  private tileKind(x: number, y: number): P.TileKind {
    if (y < this.edgeY) return "concrete"; // nivel de trabajo elevado
    if (x <= 2 && y >= 7) return "planks"; // acceso / caseta vestuario
    if (x >= 9 && y >= 5) return "sand"; // zona de materiales
    if (Math.abs(x - (y - 1)) < 1.2) return "hazard"; // pasillo de circulación
    if (y >= 8) return "path";
    return "dirt";
  }

  protected drawFloor(): void {
    const g = this.add.graphics().setDepth(-10000);
    for (let x = 0; x < this.grid.w; x++) {
      for (let y = 0; y < this.grid.h; y++) {
        const { sx, sy } = this.iso(x, y);
        P.paintTile(g, sx, sy, this.tileKind(x, y), x, y);
      }
    }
  }

  // ---------- decorado ambiental (con profundidad) ----------
  protected buildDecor(): void {
    this.decor.forEach((d) => d.destroy());
    this.decor = [];

    // plataforma elevada = "primer forjado" del edificio, con el borde sin proteger
    this.buildPlatform();

    // grúa de fondo (estructura alta)
    this.buildCrane(11.5, 0.5);

    // andamios (altos: para probar que EPI pasa por delante/detrás)
    this.prop(P.scaffold(this, 2), 1.4, 3.4);
    this.prop(P.scaffold(this, 2), 9.6, 2.2);

    // vallado del acceso
    for (const [vx, vy] of [[2, 9.2], [3.4, 9.4], [4.8, 9.5]] as [number, number][]) this.prop(P.fence(this), vx, vy);
    this.prop(P.warningSign(this, "generic"), 2.2, 8.4);

    // zona de materiales
    this.prop(P.crateStack(this), 10.4, 3.2);
    this.prop(P.barrel(this, 0xc4632a), 11.4, 2.7);
    this.prop(P.barrel(this, 0x4a7ba0), 12, 3);
    this.prop(P.pipes(this), 9.2, 8.2);
    this.prop(P.mixer(this), 13, 5.6);
    this.prop(P.wheelbarrow(this), 6.2, 8.4);
    this.prop(P.crate(this), 2.2, 6.2);
    this.prop(P.extinguisher(this), 13.4, 4.2);

    // conos delimitando el pasillo de circulación
    for (const [cx, cy] of [[5.2, 5.6], [6.6, 6.6], [7.8, 4.6]] as [number, number][]) this.prop(P.cone(this), cx, cy);

    // --- más obra: andamio, materiales, herramientas (densidad) ---
    this.prop(P.scaffold(this, 2), 12.8, 3.4);
    this.prop(P.sacks(this), 10.6, 8.4);           // sacos de cemento
    this.prop(P.pallet(this), 11.6, 5.4);
    this.prop(P.crateStack(this), 13, 8.4);
    this.prop(P.pipes(this), 2.2, 6.8);            // ferralla/tubos
    this.prop(P.toolbox(this), 6.6, 8.7);
    this.prop(P.toolbox(this, 0xd8a800), 4.2, 6.2);
    this.prop(P.hosecoil(this, 0x2a2f36), 4.6, 8.8);
    this.prop(P.barrel(this, 0x3a7a3a), 13.4, 7.0);
    this.prop(P.wheelbarrow(this), 9.4, 6.2);

    // detalle de suelo: huellas y cemento derramado
    const gd = this.add.graphics().setDepth(-9400);
    for (const [fx, fy] of [[6, 7.5], [7.5, 6.5], [9, 8]] as [number, number][]) { const p = this.iso(fx, fy), cy = p.sy + TILE_H / 2; gd.fillStyle(0x6b4a2e, 0.16); for (let k = 0; k < 3; k++) gd.fillEllipse(p.sx + k * 10 - 10, cy + (k % 2) * 5, 5, 3); }
    { const p = this.iso(11, 8.4); gd.fillStyle(0xcfc7b6, 0.35); gd.fillEllipse(p.sx, p.sy + TILE_H / 2, 26, 12); }
    this.decor.push(gd);

    // polvo de cemento subiendo de la hormigonera (13,5.6)
    this.addRisers(13, 5.6, { count: 3, color: 0xd8cdb4, alpha: 0.2, r: 4, rise: 26, sp: 0.004, grow: 1.7, dyBase: 4, depth: 30000 });

    if (this.state.chapter("obras").done) this.onResolved();
  }

  private prop(obj: Phaser.GameObjects.Container, wx: number, wy: number, dz = 0): void {
    this.addProp(obj, wx, wy, dz);
    this.decor.push(obj);
  }

  /** Forjado elevado sobre andamios (edificio en construcción) con el borde abierto. */
  private buildPlatform(): void {
    const g = this.add.graphics();
    const { hw, hd, th, lift } = this.plat;
    // sombra proyectada de la estructura
    g.fillStyle(0x000000, 0.16); g.fillEllipse(0, hd, hw * 1.5, hd * 0.7);
    // patas de andamio (del suelo al forjado)
    g.lineStyle(6, 0x8f8a7e, 1);
    for (const [lx, ly] of [[-hw, 0], [0, hd], [hw, 0]] as [number, number][]) {
      g.beginPath(); g.moveTo(lx, ly); g.lineTo(lx, ly - lift - th); g.strokePath();
    }
    // riostras diagonales entre patas
    g.lineStyle(3, 0x8f8a7e, 0.8);
    g.beginPath();
    g.moveTo(-hw, -lift); g.lineTo(0, hd - lift * 0.4);
    g.moveTo(0, hd - lift * 0.4); g.lineTo(hw, -lift);
    g.strokePath();
    // forjado (losa) elevado
    P.isoBox(g, hw, hd, th, 0xc2bdb0, { stroke: 0x8a8578, oy: -lift });
    // borde SIN barandilla: línea roja en la arista frontal superior del forjado
    g.lineStyle(3, 0xb23a2a, 0.95);
    g.beginPath();
    g.moveTo(-hw, -lift - th); g.lineTo(0, hd - lift - th); g.lineTo(hw, -lift - th);
    g.strokePath();
    const c = this.add.container(0, 0, [g]);
    const { sx, sy } = this.iso(6, 1.4);
    c.setPosition(sx, sy).setDepth(isoDepth(6, 1.4) - 2);
    this.platform = c;
    this.decor.push(c);
  }

  private buildCrane(wx: number, wy: number): void {
    const g = this.add.graphics();
    g.lineStyle(7, 0x6b4a2e, 1);
    g.beginPath();
    g.moveTo(0, 0); g.lineTo(0, -240); // mástil
    g.moveTo(-70, -220); g.lineTo(150, -230); // pluma
    g.moveTo(0, -240); g.lineTo(-70, -220);
    g.moveTo(0, -240); g.lineTo(150, -230);
    g.strokePath();
    g.fillStyle(0xf0a91b, 1);
    g.fillRect(-10, -236, 18, 20);
    g.lineStyle(3, 0x6b4a2e, 1);
    g.beginPath(); g.moveTo(110, -228); g.lineTo(110, -180); g.strokePath();
    g.fillStyle(0xdf5f2a, 1);
    g.fillRect(96, -180, 30, 22);
    const c = this.add.container(0, 0, [g]);
    const { sx, sy } = this.iso(wx, wy);
    c.setPosition(sx, sy).setDepth(isoDepth(wx, wy) - 5);
    this.decor.push(c);
  }

  // ---------- props reales por interactivo (hitbox invisible + sprite) ----------
  protected buildInteractableArt(def: InteractableDef): Phaser.GameObjects.GameObject[] {
    switch (def.id) {
      case "casco": return [P.helmet(this)];
      case "escalera": return [P.ladder(this)];
      case "herramienta": return [P.fallenTool(this)];
      case "borde": return [P.warningSign(this, "fall")];
      case "material": return [P.sacks(this)];
      case "registro": return [P.hut(this)];
      case "taquilla": return [P.locker(this)];
      case "antonio": return [P.worker(this, { shirt: 0xb06a3a, hair: 0x5a3a24 })];
      case "lucia": return [P.worker(this, { shirt: 0x2f7d78, vest: 0xe6d020, hair: 0x3a2a20 })];
      case "manolo": return [P.worker(this, { shirt: 0x5a5f68, helmet: 0xf0c400, hair: 0x8a8a90 })];
      case "salida": return [this.buildGate()];
      default: return super.buildInteractableArt(def);
    }
  }

  private buildGate(): Phaser.GameObjects.Container {
    const g = this.add.graphics();
    g.fillStyle(0x000000, 0.2); g.fillEllipse(0, 26, 60, 20);
    g.fillStyle(0x5a5f68, 1); g.fillRect(-30, -54, 6, 54); g.fillRect(24, -54, 6, 54);
    g.lineStyle(4, 0xd8b93f, 1); g.beginPath(); g.moveTo(-27, -54); g.lineTo(27, -54); g.strokePath();
    g.fillStyle(0x2f8f57, 1); g.fillTriangle(0, -78, -12, -60, 12, -60);
    g.fillStyle(0xffffff, 1); g.fillRect(-3, -68, 6, 10);
    return this.add.container(0, 0, [g]);
  }

  // ---------- al resolver: aparece la barandilla ----------
  protected onResolved(): void {
    if (!this.platform) return;
    const { hw, hd, th, lift } = this.plat;
    const topY = -lift - th;      // altura de la arista en los laterales
    const frontY = hd - lift - th; // vértice frontal
    const g = this.add.graphics();
    // barandilla verde (dos travesaños) sobre la arista frontal del forjado
    g.lineStyle(4, 0x2f8f57, 1);
    for (const dy of [-30, -16]) {
      g.beginPath();
      g.moveTo(-hw, topY + dy); g.lineTo(0, frontY + dy); g.lineTo(hw, topY + dy);
      g.strokePath();
    }
    // montantes verticales
    g.lineStyle(3, 0x2f8f57, 1);
    for (const t of [-1, -0.5, 0, 0.5, 1]) {
      const ex = t * hw;
      const ey = frontY - Math.abs(t) * hd;
      g.beginPath(); g.moveTo(ex, ey); g.lineTo(ex, ey - 30); g.strokePath();
    }
    this.platform.add(g);
  }
}
